import React from 'react';
import { useNavigate } from 'react-router-dom';
import { pickRelevantServices } from '../data/kmsServices';
import {
  Wallet, ShieldCheck, TrendingUp, HeartPulse, Sprout,
  GraduationCap, Building2, ArrowRight, Sparkles,
} from 'lucide-react';

const ICONS = {
  loans: Wallet,
  insurance: ShieldCheck,
  savings: TrendingUp,
  health: HeartPulse,
  'kisan-bazaar': Sprout,
  skills: GraduationCap,
  business: Building2,
};

const ACCENT = {
  emerald: { chip: 'bg-emerald-100 text-emerald-700', hover: 'hover:border-emerald-400 hover:shadow-emerald-100', arrow: 'text-emerald-600' },
  sky:     { chip: 'bg-sky-100 text-sky-700',         hover: 'hover:border-sky-400 hover:shadow-sky-100',         arrow: 'text-sky-600' },
  amber:   { chip: 'bg-amber-100 text-amber-700',     hover: 'hover:border-amber-400 hover:shadow-amber-100',     arrow: 'text-amber-600' },
  rose:    { chip: 'bg-rose-100 text-rose-700',       hover: 'hover:border-rose-400 hover:shadow-rose-100',       arrow: 'text-rose-600' },
  green:   { chip: 'bg-green-100 text-green-700',      hover: 'hover:border-green-400 hover:shadow-green-100',     arrow: 'text-green-600' },
  indigo:  { chip: 'bg-indigo-100 text-indigo-700',   hover: 'hover:border-indigo-400 hover:shadow-indigo-100',   arrow: 'text-indigo-600' },
  violet:  { chip: 'bg-violet-100 text-violet-700',   hover: 'hover:border-violet-400 hover:shadow-violet-100',   arrow: 'text-violet-600' },
};

/**
 * Surfaces up to `max` KMS Services cards most relevant to `text`, each
 * deep-linking to its /money-hub/{slug} detail page. Renders nothing when
 * no service matches — so it's safe to drop anywhere.
 */
export const ContextualServices = ({
  text,
  max = 3,
  heading = 'Services that can help you',
  subheading = 'Curated government schemes and trusted partners matched to your needs',
}) => {
  const navigate = useNavigate();
  const services = pickRelevantServices(text, max);
  if (!services.length) return null;

  return (
    <section data-testid="contextual-services" className="mt-2">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-5 h-5 text-emerald-600" />
        <h2 className="text-base font-semibold text-gray-900">{heading}</h2>
      </div>
      <p className="text-xs text-gray-500 mb-4">{subheading}</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {services.map((svc) => {
          const Icon = ICONS[svc.slug] || Sparkles;
          const a = ACCENT[svc.accent] || ACCENT.emerald;
          return (
            <button
              key={svc.slug}
              data-testid={`contextual-service-${svc.slug}`}
              onClick={() => navigate(`/money-hub/${svc.slug}`)}
              className={`group text-left bg-white rounded-xl border border-gray-200 p-4 transition-all duration-200 hover:shadow-md ${a.hover}`}
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${a.chip}`}>
                <Icon className="w-5 h-5" />
              </div>
              <h3 className="text-sm font-semibold text-gray-900">{svc.title}</h3>
              <p className="text-xs text-gray-500 mt-1 leading-relaxed line-clamp-2">{svc.subtitle}</p>
              <span className={`mt-3 inline-flex items-center gap-1 text-xs font-semibold ${a.arrow}`}>
                Explore
                <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default ContextualServices;
